import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export interface BankAccount {
  id: number;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  branch_name: string;
  account_type: 'savings' | 'current' | string;
  is_default: boolean;
  status: string;
  verified_at: string | null;
  verification_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface BankAccountsResponse {
  success: boolean;
  count: number;
  page: number;
  page_size: number;
  bank_accounts: BankAccount[];
}

export const fetchBankAccounts = createAsyncThunk(
  'bankAccounts/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/admin/bank-accounts/');
      return res.data as BankAccountsResponse;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.detail || e.message);
    }
  }
);

export type VerifyStatusPayload =
  | { status: 'verified'; verification_notes?: string }
  | { status: 'rejected'; rejection_reason: string; admin_notes?: string };

export const reviewBankAccount = createAsyncThunk(
  'bankAccounts/review',
  async (
    args: { id: number; payload: VerifyStatusPayload },
    { rejectWithValue }
  ) => {
    const { id, payload } = args;
    try {
      const res = await api.post(`/admin/bank-accounts/${id}/verify/`, payload);
      return res.data as { success: boolean; message: string; bank_account: BankAccount };
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.detail || e.message);
    }
  }
);

interface State {
  items: BankAccount[];
  loading: boolean;
  error: string | null;
}

const initialState: State = {
  items: [],
  loading: false,
  error: null,
};

const slice = createSlice({
  name: 'bankAccounts',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchBankAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBankAccounts.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as BankAccountsResponse;
        state.items = payload?.bank_accounts ?? [];
      })
      .addCase(fetchBankAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(reviewBankAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reviewBankAccount.fulfilled, (state, action) => {
        state.loading = false;
        const updated = (action.payload as any).bank_account as BankAccount;
        const idx = state.items.findIndex(i => i.id === updated.id);
        if (idx >= 0) {
          state.items[idx] = updated;
        } else {
          state.items.unshift(updated);
        }
      })
      .addCase(reviewBankAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export default slice.reducer;


